import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type NavigationGuardNext
} from 'vue-router'
import { storeToRefs } from 'pinia'
import routes from '@/router/routes'
import useEditionStageStore from '@/stores/editionStage'
import { EditionStage } from '@/types'

const base =
  import.meta.env.VITE_APP_PUBLIC_PATH === undefined ? '/' : import.meta.env.VITE_APP_PUBLIC_PATH

const router = createRouter({
  routes,
  history: createWebHistory(base),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }

    const comeToListFromChildPage =
      ['Home'].includes(String(to.name)) &&
      ['ChildPage', 'ChildPageRetention'].includes(String(from.name))

    if (comeToListFromChildPage) {
      const targetElement: HTMLElement | null = document.getElementById('children-list')

      if (targetElement) {
        // Promise is needed to make sure that the targetElement has its position calculated
        // https://router.vuejs.org/guide/advanced/scroll-behavior#Delaying-the-scroll
        return new Promise((resolve) => {
          setTimeout(() => {
            resolve({ el: targetElement })
          }, 100)
        })
      }
    } else return { top: 0 }
  }
})

function setMetaTagsFromRouter(
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) {
  document.title = to.meta.title ? String(to.meta.title) : 'Ścieżka Wyboru Dzieci'

  const nearestWithMeta = to.matched
    .slice()
    .reverse()
    .find((r) => r.meta && r.meta.metaTags)
  Array.from(document.querySelectorAll('[data-vue-router-controlled]')).map((el) =>
    el?.parentNode?.removeChild(el)
  )

  if (!nearestWithMeta) {
    return next()
  }

  ;(nearestWithMeta.meta.metaTags as string[])
    .map((tagDef) => {
      const tag = document.createElement('meta')

      Object.keys(tagDef).forEach((key) => {
        tag.setAttribute(key, tagDef[key as unknown as number])
      })

      tag.setAttribute('data-vue-router-controlled', '')
      return tag
    })
    .forEach((tag) => document.head.appendChild(tag))

  return next()
}

router.beforeResolve(async (to, from, next) => {
  const editionStageStore = useEditionStageStore()
  const { getEditionStage } = storeToRefs(editionStageStore)
  // fetch edition stage from db if is undefined
  if (!getEditionStage.value) {
    try {
      await editionStageStore.fetchEditionStage(router)
    } catch {
      // redirect to ClosedBefore if backend throw error or response validation fails
      return next({ name: 'ClosedBefore' })
    }
  }
  // check if entering page should have edition stage guard and redirect to closedPage if needed
  const pagesToCheckStage = ['Home']
  if (pagesToCheckStage.includes(String(to.name))) {
    if (getEditionStage.value === EditionStage.beforeCampaign) {
      return next({ name: 'ClosedBefore' })
    }
    if (getEditionStage.value === EditionStage.retention) {
      return next({ name: 'ClosedBefore' })
    }
    if (getEditionStage.value === EditionStage.campaign) {
      return next()
    }
    if (getEditionStage.value === EditionStage.afterCampaign) {
      return next({ name: 'ClosedAfter' })
    }
    return next({ name: 'ClosedBefore' })
  }
  return next()
})
router.beforeEach((to, from, next) => {
  setMetaTagsFromRouter(to, from, next)
})
export default router
